import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosResponse } from 'axios';

import type { ITagProps } from 'libs/design-system/src/types/tag/tag';

import type { ISharedEmail } from '@app/dashboard/types/innerLayout/reports/modals/share';
import {
  TCustomReportData,
  TOption,
} from '@app/dashboard/types/innerLayout/reports';
import type { TAppliedRanges } from '@app/dashboard/types/innerLayout/date-ranges';
import { mainPreDefinedRanges } from '@app/dashboard/utils/dateRangePicker';
import { shareReport } from '@app/dashboard/services/inner_layout/reports';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { getMultiSelectorBtnLabel } from '../utils';

import { CustomReportsModalBody } from './CustomReportsModalBody';
import { ShareModalBody } from './ShareModalBody';

export const RenderCustomReportsModalBody = ({
  customReportData,
  setCustomReportData,
  transformedColumns,
  lang,
  handleDateRangeChange,
  containerRef
}: {
  customReportData: TCustomReportData;
  setCustomReportData: Dispatch<SetStateAction<TCustomReportData>>;
  transformedColumns: { label: string; value: string }[];
  lang: 'ar' | 'en';
  handleDateRangeChange: (
    newRanges: TAppliedRanges,
    isCustomReport?: boolean
  ) => void;
  containerRef: React.RefObject<HTMLDialogElement>;
}) => {
  const { t } = useTranslation();

  const handleCustomReportNameChange = (name: string) => {
    setCustomReportData((prev) => ({ ...prev, report_name: name }));
  };

  const handleCustomReportColumnsChange = (columns: TOption[]) => {
    setCustomReportData((prev) => ({ ...prev, selected_columns: columns }));
  };

  const handleCustomReportSaveAsGeneralChange = (checked: boolean) => {
    setCustomReportData((prev) => ({ ...prev, save_template: checked }));
  };

  return (
    <CustomReportsModalBody
      containerRef={containerRef}
      handleRangesChange={(ranges) => handleDateRangeChange(ranges, true)}
      appliedRanges={customReportData?.ranges}
      lang={lang}
      localization={{
        name: t('innerLayout.reportsPage.customReport.name'),
        nameInputPlaceholder: t(
          'innerLayout.reportsPage.customReport.namePlaceholder'
        ),
        dateRange: t('innerLayout.reportsPage.customReport.dateRange'),
        columns: t('innerLayout.reportsPage.customReport.columns'),
        saveAsGeneral: t('innerLayout.reportsPage.customReport.saveAsGeneral'),
        columnsPlaceholder: t(
          'innerLayout.reportsPage.customReport.columnsPlaceholder'
        ),
        selected: t('innerLayout.selected'),
      }}
      buttonLabel={
        getMultiSelectorBtnLabel(
          customReportData.selected_columns || [],
          transformedColumns,
          t,
          t(`innerLayout.reportsPage.customReport.allColumns`)
        ) || ''
      }
      datePickerLabel={t('innerLayout.reportsPage.dateRangePicker.label')}
      onNameChange={handleCustomReportNameChange}
      onColumnsChange={handleCustomReportColumnsChange}
      onSaveAsGeneralChange={handleCustomReportSaveAsGeneralChange}
      availableColumns={transformedColumns}
      preDefinedRanges={mainPreDefinedRanges(t)}
    />
  );
}

export const RenderShareModalBody = ({
  exportResponse,
  setSharedList,
  sharedList,
}: {
  exportResponse: AxiosResponse<any, any> | undefined;
  setSharedList: Dispatch<SetStateAction<ISharedEmail[]>>;
  sharedList: ISharedEmail[];
}) => {
  const { t } = useTranslation();

  return (
    <ShareModalBody
      mutation={{
        mutationFunction: shareReport,
        mutationFunctionParams: { id: exportResponse?.data?.data?.report_id },
        mutationKey: [QUERY_KEYS.SHARE_REPORT],
        onSuccessHandler: (_, tags: ITagProps[]) => {
          const newSharedItems = tags.map((tag) => ({
            email: tag.text,
            status: t('innerLayout.reportsPage.sent'),
          }));
          setSharedList((prevList) => [...prevList, ...newSharedItems]);
        },
      }}
      localization={{
        inputLabel: t('innerLayout.reportsPage.share.inputLabel'),
        inputPlaceholder: t('innerLayout.reportsPage.share.inputPlaceholder'),
        shareButtonText: t('innerLayout.reportsPage.share.shareButton'),
        errors: {
          invalidEmail: t('errors.invalidEmail'),
          emailAlreadyAdded: t('errors.emailAlreadyAdded'),
          emailAlreadyShared: t('errors.emailAlreadyShared'),
          shareFailed: t('errors.shareFailed'),
        },
      }}
      sharedList={sharedList}
    />
  );
};
